import {
  faChevronDown,
  faChevronRight,
  faEye,
  faPen,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IssueUpdate } from "@boark/shared/types/issue-update";
import { IssueState, IssueType } from "@boark/shared/types/issue";
import { IssueWithCreatedByAndLinksAndChildrenAndParentAndRegisters } from "@boark/shared/types/issue-with-created-by-and-links-and-children-and-parent-and-registers";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  Link,
  Outlet,
  Route,
  Routes,
  useParams,
} from "react-router-dom";

import { Button } from "../../components/button";
import { H1 } from "../../components/h1";
import { useGetIssues } from "../../queries/issue";
import { updateIssue } from "../../services/issue";
import { useLocale } from "../../stores/locale";
import {
  BoardCard,
  BoardColumn,
  boardColumnToIssueState,
  buildIssueBoardSwimlanes,
  canMoveIssueToSwimlane,
  getParentIdentifierForSwimlane,
  groupCardsByColumn,
  issueStateToBoardColumn,
} from "../../utils/issue-board-data";
import { IssueCreate } from "./issue-create";
import { IssueEdit } from "./issue-edit";
import { IssueView } from "./issue-view";

const DRAG_ISSUE_ID_KEY = "issue-board/identifier";
const DRAG_FROM_COLUMN_KEY = "issue-board/from-column";
const DRAG_FROM_SWIMLANE_KEY = "issue-board/from-swimlane-id";
const DRAG_CURRENT_STATE_KEY = "issue-board/current-state";
const DRAG_ISSUE_TYPE_KEY = "issue-board/issue-type";

export function IssueBoard() {
  const { locale: t } = useLocale();
  const { data: issues } = useGetIssues();
  const [collapsedLaneIds, setCollapsedLaneIds] = useState<Set<string>>(
    () => new Set(),
  );

  const swimlanes = useMemo(
    () =>
      issues
        ? buildIssueBoardSwimlanes(issues, t.issue.issuesWithoutParent)
        : [],
    [issues, t.issue.issuesWithoutParent],
  );

  const columnLabels: Record<BoardColumn, string> = {
    todo: t.issue.stateTodo,
    inprogress: t.issue.stateInProgress,
    "done-wontdo": t.issue.stateDoneWontdo,
  };

  const toggleLane = (laneId: string) => {
    setCollapsedLaneIds((current) => {
      const next = new Set(current);
      if (next.has(laneId)) {
        next.delete(laneId);
      } else {
        next.add(laneId);
      }
      return next;
    });
  };

  return (
    <>
      <Routes>
        <Route
          element={
            <IssueCreate
              defaultType={IssueType.GROUP}
              fallbackRoute="/admin/issue/board"
              typeDisabled
            />
          }
          path="/create"
        />
        <Route
          element={
            <IssueCreate fallbackRoute="/admin/issue/board" />
          }
          path="/create-issue"
        />
        <Route
          element={<IssueCreateFromSwimlane />}
          path="/create-issue/:parentIdentifier"
        />
        <Route element={<IssueEdit />} path="/:id/edit" />
        <Route element={<IssueView />} path="/:id" />
      </Routes>
      <Outlet />
      <div className="w-full min-h-full overflow-x-auto">
        <div className="flex justify-between items-center">
          <H1>{t.issue.board}</H1>
          <Button route="/admin/issue/board/create">
            {t.issue.createGroup}
          </Button>
        </div>
        {issues && (
          <div className="mt-4 min-w-[960px]">
            <div className="grid grid-cols-[minmax(220px,280px)_repeat(3,minmax(220px,1fr))] gap-3 mb-2 px-1">
              <div />
              {(Object.keys(columnLabels) as BoardColumn[]).map((column) => (
                <div
                  key={column}
                  className="text-sm font-semibold text-gray-700 px-2"
                >
                  {columnLabels[column]}
                </div>
              ))}
            </div>
            <div className="flex flex-col gap-3">
              {swimlanes.map((lane) => (
                <SwimlaneRow
                  key={lane.id}
                  collapsed={collapsedLaneIds.has(lane.id)}
                  columnLabels={columnLabels}
                  lane={lane}
                  onToggle={() => toggleLane(lane.id)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function SwimlaneRow({
  lane,
  collapsed,
  columnLabels,
  onToggle,
}: {
  lane: ReturnType<typeof buildIssueBoardSwimlanes>[number];
  collapsed: boolean;
  columnLabels: Record<BoardColumn, string>;
  onToggle: () => void;
}) {
  const { locale: t, get: getLocale } = useLocale();
  const cardsByColumn = useMemo(
    () => groupCardsByColumn(lane.cards),
    [lane.cards],
  );

  return (
    <div
      className={
        collapsed
          ? "flex"
          : "grid grid-cols-[minmax(220px,280px)_repeat(3,minmax(220px,1fr))] gap-3 items-start"
      }
    >
      <div
        className={`relative rounded-sm bg-gray-100 hover:bg-gray-200 w-full ${
          collapsed ? "min-h-0" : ""
        }`}
      >
        {lane.groupIdentifier !== undefined && (
          <div className="absolute top-1.5 right-1.5 flex items-center gap-0.5 z-10">
            <Link
              className="inline-flex items-center justify-center w-4 h-4 rounded-sm text-gray-400 text-[10px] no-underline hover:text-blue-600 hover:bg-gray-200"
              title={getLocale("viewEntity", {
                entity: t.issue.typeGroup.toLowerCase(),
              })}
              to={`/admin/issue/board/${lane.groupIdentifier}`}
            >
              <FontAwesomeIcon icon={faEye} />
            </Link>
            <Link
              className="inline-flex items-center justify-center w-4 h-4 rounded-sm text-gray-400 text-[10px] no-underline hover:text-blue-600 hover:bg-gray-200"
              title={t.page.edit}
              to={`/admin/issue/board/${lane.groupIdentifier}/edit`}
            >
              <FontAwesomeIcon icon={faPen} />
            </Link>
          </div>
        )}
        <button
          className="flex items-start gap-2 text-left px-3 py-2 cursor-pointer border-0 w-full bg-transparent"
          title={collapsed ? t.issue.boardExpandLane : t.issue.boardCollapseLane}
          type="button"
          onClick={onToggle}
        >
          <FontAwesomeIcon
            className="mt-1 shrink-0 text-gray-600"
            icon={collapsed ? faChevronRight : faChevronDown}
          />
          <div className="min-w-0 pr-9">
            <div className="font-semibold break-words">{lane.title}</div>
            {lane.groupIdentifier !== undefined && (
              <div className="text-xs text-gray-600">#{lane.groupIdentifier}</div>
            )}
            <div className="text-xs text-gray-500">
              {lane.cards.length}{" "}
              {lane.cards.length === 1 ? t.issue.boardCard : t.issue.boardCards}
            </div>
          </div>
        </button>
      </div>
      {!collapsed &&
        (Object.keys(columnLabels) as BoardColumn[]).map((column) => (
          <BoardColumnDropZone
            key={`${lane.id}-${column}`}
            cards={cardsByColumn[column]}
            column={column}
            groupIdentifier={lane.groupIdentifier}
            swimlaneId={lane.id}
          />
        ))}
    </div>
  );
}

function BoardColumnDropZone({
  column,
  cards,
  swimlaneId,
  groupIdentifier,
}: {
  column: BoardColumn;
  cards: BoardCard[];
  swimlaneId: string;
  groupIdentifier?: number;
}) {
  const { locale: t } = useLocale();
  const [isDragOver, setIsDragOver] = useState(false);
  const updateIssueBoardCard = useUpdateIssueBoardCard();

  const createIssueRoute =
    groupIdentifier !== undefined
      ? `/admin/issue/board/create-issue/${groupIdentifier}`
      : "/admin/issue/board/create-issue";

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);

    const identifier = Number.parseInt(
      event.dataTransfer.getData(DRAG_ISSUE_ID_KEY),
      10,
    );
    const fromColumn = event.dataTransfer.getData(
      DRAG_FROM_COLUMN_KEY,
    ) as BoardColumn;
    const fromSwimlaneId = event.dataTransfer.getData(DRAG_FROM_SWIMLANE_KEY);
    const currentState = event.dataTransfer.getData(
      DRAG_CURRENT_STATE_KEY,
    ) as IssueState;
    const issueType = event.dataTransfer.getData(
      DRAG_ISSUE_TYPE_KEY,
    ) as IssueType;

    if (
      !Number.isFinite(identifier) ||
      !fromColumn ||
      !fromSwimlaneId ||
      !currentState ||
      !issueType
    ) {
      return;
    }

    const parentChanged = fromSwimlaneId !== swimlaneId;
    const nextState = boardColumnToIssueState(column, currentState);
    const stateChanged =
      fromColumn !== column ||
      issueStateToBoardColumn(currentState) !== column;

    if (!parentChanged && !stateChanged) {
      return;
    }

    if (parentChanged && !canMoveIssueToSwimlane(issueType, swimlaneId)) {
      return;
    }

    const update: IssueUpdate = {};
    if (stateChanged) {
      update.state = nextState;
    }
    if (parentChanged) {
      update.parentIdentifier = getParentIdentifierForSwimlane(
        swimlaneId,
        groupIdentifier,
      );
    }

    updateIssueBoardCard.mutate({ identifier, update });
  };

  return (
    <div
      className={`min-h-[120px] rounded-sm border border-dashed p-2 flex flex-col gap-2 transition-colors ${
        isDragOver
          ? "border-blue-500 bg-blue-50"
          : "border-gray-300 bg-gray-50"
      }`}
      data-swimlane={swimlaneId}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {column === "todo" && (
        <Link
          className="flex items-center justify-center gap-1 w-full rounded-sm border border-dashed border-gray-300 bg-white py-1.5 text-xs text-gray-600 no-underline hover:border-blue-400 hover:text-blue-600"
          title={t.issue.boardAddIssue}
          to={createIssueRoute}
        >
          <FontAwesomeIcon icon={faPlus} />
          {t.issue.create}
        </Link>
      )}
      {cards.map((card) => (
        <IssueBoardCard
          key={card.identifier}
          card={card}
          column={column}
          swimlaneId={swimlaneId}
        />
      ))}
    </div>
  );
}

function IssueCreateFromSwimlane() {
  const { parentIdentifier } = useParams();

  if (!parentIdentifier) {
    throw new Error("Parent identifier is required");
  }

  const parsedParentIdentifier = Number.parseInt(parentIdentifier, 10);
  if (!Number.isInteger(parsedParentIdentifier) || parsedParentIdentifier <= 0) {
    throw new Error("Invalid parent identifier");
  }

  return (
    <IssueCreate
      defaultParentIdentifier={parsedParentIdentifier}
      fallbackRoute="/admin/issue/board"
      parentDisabled
    />
  );
}

function IssueBoardCard({
  card,
  column,
  swimlaneId,
}: {
  card: BoardCard;
  column: BoardColumn;
  swimlaneId: string;
}) {
  const { locale: t } = useLocale();

  const typeLabel =
    card.type === IssueType.GROUP
      ? t.issue.typeGroup
      : card.type === IssueType.TASK
        ? t.issue.typeTask
        : t.issue.typeIssue;

  const stateLabel =
    card.state === IssueState.WONTDO ? t.issue.stateWontdo : undefined;

  const handleDragStart = (event: React.DragEvent<HTMLDivElement>) => {
    event.dataTransfer.setData(DRAG_ISSUE_ID_KEY, String(card.identifier));
    event.dataTransfer.setData(DRAG_FROM_COLUMN_KEY, column);
    event.dataTransfer.setData(DRAG_FROM_SWIMLANE_KEY, swimlaneId);
    event.dataTransfer.setData(DRAG_CURRENT_STATE_KEY, card.state);
    event.dataTransfer.setData(DRAG_ISSUE_TYPE_KEY, card.type);
    event.dataTransfer.effectAllowed = "move";
  };

  return (
    <div
      draggable
      className="rounded-sm border border-gray-200 bg-white shadow-sm px-3 py-2 cursor-grab active:cursor-grabbing"
      onDragStart={handleDragStart}
    >
      <div className="flex justify-end -mt-1 -mr-1.5 mb-0.5">
        <Link
          className="inline-flex items-center justify-center w-4 h-4 rounded-sm text-gray-400 text-[10px] no-underline hover:text-blue-600 hover:bg-gray-100"
          draggable={false}
          title={t.page.edit}
          to={`/admin/issue/board/${card.identifier}/edit`}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <FontAwesomeIcon icon={faPen} />
        </Link>
      </div>
      <Link
        className="block no-underline text-inherit hover:text-blue-600"
        draggable={false}
        to={`/admin/issue/board/${card.identifier}`}
      >
        <div className="text-xs text-gray-500 flex justify-between gap-2">
          <span>
            #{card.identifier} · {typeLabel}
          </span>
          {stateLabel && (
            <span className="shrink-0 font-medium">{stateLabel}</span>
          )}
        </div>
        <div className="font-medium text-sm break-words">{card.title}</div>
      </Link>
    </div>
  );
}

function useUpdateIssueBoardCard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      identifier,
      update,
    }: {
      identifier: number;
      update: IssueUpdate;
    }) => updateIssue(identifier, update),
    onMutate: async ({ identifier, update }) => {
      await queryClient.cancelQueries({ queryKey: ["issues"] });
      const previousIssues = queryClient.getQueryData<
        IssueWithCreatedByAndLinksAndChildrenAndParentAndRegisters[]
      >(["issues"]);

      if (previousIssues) {
        queryClient.setQueryData(
          ["issues"],
          applyIssueBoardUpdateToIssues(previousIssues, identifier, update),
        );
      }

      return { previousIssues };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousIssues) {
        queryClient.setQueryData(["issues"], context.previousIssues);
      }
    },
    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["issues"] });
      void queryClient.invalidateQueries({
        queryKey: ["issue", String(variables.identifier)],
      });
    },
  });
}

function applyIssueBoardUpdateToIssues(
  issues: IssueWithCreatedByAndLinksAndChildrenAndParentAndRegisters[],
  identifier: number,
  update: IssueUpdate,
): IssueWithCreatedByAndLinksAndChildrenAndParentAndRegisters[] {
  const updatedAt = new Date();
  const parentChanged = update.parentIdentifier !== undefined;
  const parentGroup =
    update.parentIdentifier != null
      ? issues.find(
          (issue) =>
            issue.type === IssueType.GROUP &&
            issue.identifier === update.parentIdentifier,
        )
      : undefined;

  const topLevelIssue = issues.find((issue) => issue.identifier === identifier);
  const existingIssue =
    topLevelIssue ??
    issues
      .flatMap((issue) => issue.children)
      .find((child) => child.identifier === identifier);

  if (!existingIssue) {
    return issues;
  }

  const nextChild = {
    ...existingIssue,
    ...(update.state !== undefined && { state: update.state }),
    updatedAt,
  };

  const nextParent =
    update.parentIdentifier === undefined
      ? undefined
      : update.parentIdentifier === null
        ? undefined
        : parentGroup
          ? {
              identifier: parentGroup.identifier,
              title: parentGroup.title,
              description: parentGroup.description,
              tags: parentGroup.tags,
              state: parentGroup.state,
              type: parentGroup.type,
              createdAt: parentGroup.createdAt,
              updatedAt: parentGroup.updatedAt,
            }
          : topLevelIssue?.parent;

  return issues.map((issue) => {
    let children = issue.children;

    if (parentChanged) {
      children = children.filter((child) => child.identifier !== identifier);
      if (
        parentGroup &&
        issue.identifier === parentGroup.identifier &&
        !children.some((child) => child.identifier === identifier)
      ) {
        children = [...children, nextChild];
      }
    } else {
      children = children.map((child) =>
        child.identifier === identifier ? nextChild : child,
      );
    }

    if (issue.identifier !== identifier) {
      return { ...issue, children };
    }

    return {
      ...issue,
      ...(update.state !== undefined && { state: update.state }),
      ...(parentChanged && { parent: nextParent }),
      updatedAt,
      children,
    };
  });
}
